import React, {useState, useEffect, useCallback} from 'react';
import {View, StyleSheet, SectionList} from 'react-native';
import {Card, Text, Menu, Button, Chip, ActivityIndicator} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import DateRangeFilter from './components/DateRangeFilter';
import {useReportStore} from '../../store/useReportStore';
import {colors, spacing} from '../../theme';

const ORDER_STATUSES = [
  'NEW', 'ASSIGNED', 'CUTTING', 'STITCHING', 'FINISHING',
  'QUALITY_CHECK', 'READY_FOR_PICKUP', 'DELIVERED',
];

const formatDateStr = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const statusColors = colors.status || {};

const ReportDuesTab = ({orgUnitId}) => {
  const {t} = useTranslation();
  const {dues, isLoading, fetchDues} = useReportStore();
  const now = new Date();
  const [fromDate, setFromDate] = useState(formatDateStr(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [toDate, setToDate] = useState(formatDateStr(now));
  const [statusFilter, setStatusFilter] = useState(null);
  const [menuVisible, setMenuVisible] = useState(false);

  const loadData = useCallback(() => {
    if (fromDate && toDate) {
      const statuses = statusFilter ? [statusFilter] : undefined;
      fetchDues(fromDate, toDate, statuses, orgUnitId);
    }
  }, [fromDate, toDate, statusFilter, orgUnitId, fetchDues]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const sections = (dues?.groups || []).map(group => ({
    title: group.customerName,
    subtotal: group.subtotal,
    data: group.rows,
  }));

  const formatCurrency = (val) => `₹${Number(val).toLocaleString('en-IN')}`;

  const renderSectionHeader = ({section}) => (
    <View style={styles.sectionHeader}>
      <Text style={styles.sectionTitle}>{section.title}</Text>
      <Text style={styles.sectionSubtotal}>{formatCurrency(section.subtotal)}</Text>
    </View>
  );

  const renderItem = ({item}) => (
    <View style={styles.row}>
      <View style={styles.rowLeft}>
        <Text style={styles.rowDate}>{item.orderDate}</Text>
        <Text style={styles.rowOrderNum}>{item.orderNumber}</Text>
      </View>
      <View style={styles.rowRight}>
        <Text style={styles.rowTotal}>{formatCurrency(item.totalAmount)}</Text>
        <Text style={styles.rowBalance}>Due: {formatCurrency(item.balanceAmount)}</Text>
        <Chip
          compact
          style={[styles.statusChip, {backgroundColor: (statusColors[item.status] || colors.textSecondary) + '20'}]}>
          <Text style={{fontSize: 13, color: '#000', marginTop: 3}}>{item.status}</Text>
        </Chip>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <DateRangeFilter
        fromDate={fromDate}
        toDate={toDate}
        onFromChange={setFromDate}
        onToChange={setToDate}
      />

      <View style={styles.statusFilterRow}>
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <Button
              mode="outlined"
              compact
              onPress={() => setMenuVisible(true)}
              icon="filter-variant">
              {statusFilter || t('report.allStatuses')}
            </Button>
          }>
          <Menu.Item
            onPress={() => { setStatusFilter(null); setMenuVisible(false); }}
            title={t('report.allStatuses')}
          />
          {ORDER_STATUSES.map(s => (
            <Menu.Item
              key={s}
              onPress={() => { setStatusFilter(s); setMenuVisible(false); }}
              title={s}
            />
          ))}
        </Menu>
      </View>

      {isLoading ? (
        <ActivityIndicator style={styles.loader} size="large" color={colors.primary} />
      ) : sections.length > 0 ? (
        <>
          <Card style={styles.grandTotalCard} mode="elevated">
            <Card.Content style={styles.grandTotalContent}>
              <Text style={styles.grandTotalLabel}>{t('report.grandTotal')}</Text>
              <Text style={styles.grandTotalValue}>{formatCurrency(dues?.grandTotal || 0)}</Text>
            </Card.Content>
          </Card>
          <SectionList
            sections={sections}
            keyExtractor={(item, index) => `${item.orderNumber}-${index}`}
            renderSectionHeader={renderSectionHeader}
            renderItem={renderItem}
            contentContainerStyle={styles.listContent}
          />
        </>
      ) : (
        <Text style={styles.noData}>{t('report.noReportData')}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  statusFilterRow: {
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
    alignItems: 'flex-start',
  },
  loader: {marginTop: spacing.xl},
  grandTotalCard: {
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    backgroundColor: colors.primary,
  },
  grandTotalContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  grandTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  grandTotalValue: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.white,
  },
  listContent: {paddingHorizontal: spacing.md, paddingBottom: spacing.xl},
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: colors.primaryLight + '20',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginTop: spacing.sm,
    borderRadius: 6,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.primary,
  },
  sectionSubtotal: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
    backgroundColor: colors.surface,
  },
  rowLeft: {flex: 1},
  rowDate: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  rowOrderNum: {
    fontSize: 13,
    color: colors.text,
    marginTop: 2,
  },
  rowRight: {
    alignItems: 'flex-end',
  },
  rowTotal: {
    fontSize: 13,
    color: colors.textSecondary,
  },
  rowBalance: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.error,
    marginTop: 2,
  },
  statusChip: {
    marginTop: 4,
    height: 24,
    paddingVertical: 0,
  },
  statusText: {
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '600',
  },
  noData: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xl,
  },
});

export default ReportDuesTab;
